import {
  createDataToUrlFilters,
  parceDataFromUrlFilters,
  changeSortParam,
} from '@/helpers/tableHelpers';
import {
  setUrlParam,
  pushHistory,
  appendUrlParams,
} from '@/helpers/urlParamsHelpers';
import {
  DATE_URL_PARAM_NAME,
  PAGE_URL_PARAM_NAME,
  PER_PAGE_URL_PARAM_NAME,
  SEPARATOR_URL_FILTER_VALUES,
  SORT_URL_PARAM_NAME,
} from '@/configs/tableConfigs';
import {
  changeDropDownListValues,
  filterArrayByParam,
  getObjectFromArray,
} from '@/helpers/dataHelpers';
import {
  TTableColumn,
  TTableColumns,
  TSelectedFilterValue,
  TSelectedFilterValues,
  TTableValuesPayload,
  TTableVisiblePayload,
} from '@/components/table/types';
import { TDropDownItem, TDictionary, TDropDownList } from '@/types';

export default class TableService {
  static sortColumn(sortColumn: string): string {
    const newUrl = setUrlParam(SORT_URL_PARAM_NAME, sortColumn);
    pushHistory(newUrl);
    return sortColumn;
  }

  static changeSortColumn(oldSortColumn: string): string {
    const newSortColumn = changeSortParam(oldSortColumn);
    const newUrl = setUrlParam(SORT_URL_PARAM_NAME, newSortColumn);
    pushHistory(newUrl);
    return newSortColumn;
  }

  static changePerPage(perPage: number | null = null): void {
    const value = perPage !== null ? String(perPage) : '';
    const newUrl = setUrlParam(PER_PAGE_URL_PARAM_NAME, value);
    pushHistory(newUrl);
  }

  static changePage(page: number | null = null): void {
    const value = page !== null ? String(page) : '';
    const newUrl = setUrlParam(PAGE_URL_PARAM_NAME, value);
    pushHistory(newUrl);
  }

  static setFilterDateValuesToUrl(date: Array<string>): void {
    const newUrl = setUrlParam(
      DATE_URL_PARAM_NAME,
      date.join(SEPARATOR_URL_FILTER_VALUES)
    );
    pushHistory(newUrl);
  }

  static setFilterValuesToUrl(
    values: TSelectedFilterValues,
    filterName: string
  ): void {
    const params = createDataToUrlFilters(values, filterName);
    const newUrl = appendUrlParams(params);
    pushHistory(newUrl);
  }

  static getFilterValuesFromRouter(
    query: TDictionary<string>,
    filterName: string
  ): TSelectedFilterValues {
    return parceDataFromUrlFilters(query, filterName);
  }

  static getColumn(
    columns: TTableColumns,
    param: string,
    value: string
  ): TTableColumn {
    return getObjectFromArray(columns, param, value);
  }

  static removeSelectedValue(
    columns: TTableColumns,
    selectedValue: TSelectedFilterValue
  ): TTableValuesPayload {
    let values = [] as TDropDownList;
    const column = getObjectFromArray(columns, 'field', selectedValue.field);

    if (column !== null) {
      values = changeDropDownListValues(column.filterValues, selectedValue);
    }

    const payload: TTableValuesPayload = {
      field: selectedValue.field,
      values,
    };

    return payload;
  }

  static filterColumnsByVisible(columns: TTableColumns): TTableColumns {
    return filterArrayByParam(columns, 'visible');
  }

  static filterColumnsByUseIsFilter(columns: TTableColumns): TTableColumns {
    return filterArrayByParam(columns, 'useInFilter');
  }

  static changeActiveColumn(
    columns: TTableColumns,
    payload: TTableVisiblePayload
  ): TTableColumns {
    const newColumns = [] as TTableColumns;
    const { field, visible } = payload;

    try {
      for (const column of columns) {
        if (column.field === field) {
          column.visible = visible;
        }

        newColumns.push(column);
      }
    } catch (err) {
      console.error('changeActiveColumn: ', err);
    }

    return newColumns;
  }

  static addNewFilterValueWithMultiple(
    columns: TTableColumns,
    payload: TTableValuesPayload,
    multiple: boolean
  ): TTableColumns {
    const newColumns = [] as TTableColumns;
    const { field, values } = payload;
    try {
      for (const column of columns) {
        if (!multiple) {
          if (column.field === field) {
            column.filterValues = values;
          } else {
            column.filterValues = [];
          }
        } else {
          if (column.field === field) {
            column.filterValues = values;
          }
        }

        newColumns.push(column);
      }
    } catch (err) {
      console.error('addNewFilterValueWithMultiple: ', err);
    }

    return newColumns;
  }

  static getFilterValues(columns: TTableColumns): TSelectedFilterValues {
    const filterValues = [] as TSelectedFilterValues;
    let columnFilterValues = [] as TDropDownList | undefined;
    let newValue = {} as TSelectedFilterValue;

    try {
      for (const column of columns) {
        columnFilterValues = column.filterValues;

        if (columnFilterValues?.length) {
          for (const item of columnFilterValues) {
            newValue = {
              name: column.name,
              field: column.field,
              label: item.label,
              value: item.value,
            };
            if (column.prefix) {
              newValue.prefix = column.prefix;
            }

            filterValues.push(newValue);
          }
        }
      }
    } catch (err) {
      console.error('getFilterValues: ', err);
    }

    return filterValues;
  }

  static addNewItemsWithMultiple(
    column: TTableColumn,
    item: TDropDownItem,
    multiple: boolean
  ): TTableValuesPayload {
    const payload = {
      field: column?.field,
      values: [],
    } as TTableValuesPayload;

    try {
      if (!multiple) {
        payload.values = [item];
      } else {
        if (column.filterValues === undefined) {
          column.filterValues = [];
        }
        payload.values = changeDropDownListValues(column?.filterValues, item);
      }
    } catch (err) {
      console.error('addNewItemsWithMultiple: ', err);
    }

    return payload;
  }

  static checkkItems(column: TTableColumn, value: string): boolean {
    const values = column?.filterValues?.map((fv) => fv.value);
    if (values?.length) {
      return values.includes(value);
    }
    return false;
  }
}
